"use client"

import type React from "react"

import { useEffect, useState, useMemo } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  IconButton,
  Collapse,
  Box,
  Typography,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormControlLabel,
  Chip,
  Tooltip,
  Button,
  Stack,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import CategoryIcon from "@mui/icons-material/Category"
import SecurityIcon from "@mui/icons-material/Security"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import { useTranslation } from "../common/components/LocalizationProvider"
import { usePremiumTokens } from "../common/theme/usePremiumTokens"
import { formatDate } from "../common/util/formatter"
import { errorsActions, sessionActions } from "../store"
import PageLayout from "../common/components/PageLayout"
import ReportFilter from "../reports/components/ReportFilter"
import ColumnSelect from "../reports/components/ColumnSelect"
import TableShimmer from "../common/components/TableShimmer"
import useReportStyles from "../reports/common/useReportStyles"
import { useCatch } from "../reactHelper"
import { utils, writeFileXLSX } from "xlsx"
import { nativePostMessage } from "../common/components/NativeInterface"
import ReportsMenu from "./components/ReportsMenu"
import UserNameFilter from "./components/UserNameFilter"
import fetchOrThrow from "../common/util/fetchOrThrow"
import { PrintLayout } from "./common/print"
import type { SelectChangeEvent } from "@mui/material/Select"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import RestoreIcon from "@mui/icons-material/Restore"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DownloadIcon from "@mui/icons-material/Download"
import AddCircleIcon from "@mui/icons-material/AddCircle"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import PersonIcon from "@mui/icons-material/Person"
import AssessmentIcon from "@mui/icons-material/Assessment"
import RecoveryDialog from "./components/RecoveryDialog"
import { AuditLogItem, AuditLogDiff, AuditLogAttributes } from "./types"

interface User {
  id: number
  name?: string
  email?: string
}

// A stable empty object used as a fallback for usersMap selectors.
// Returning the same reference avoids creating a new object on each render,
// preventing unnecessary re-renders when the state.usersMap is undefined.
const EMPTY_USERS_MAP = Object.freeze<Record<number, string | undefined>>({})
const EMPTY_DEVICE_MAP = Object.freeze<Record<number, any>>({})
const FILTER_FORM_CONTROL_SX = {
  minWidth: 240,
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    "& fieldset": { borderColor: "divider" },
    "&:hover fieldset": { borderColor: "primary.main" },
    "&.Mui-focused fieldset": { borderColor: "primary.main" },
  },
}
const FILTER_SELECT_SX = { backgroundColor: "background.paper" }

// Mapeamento de objectType para chaves de tradução existentes
const OBJECT_TYPE_TRANSLATION_KEYS: Record<string, string> = {
  device: "deviceTitle",
  user: "settingsUser",
  group: "groupDialog",
  geofence: "sharedGeofence",
  driver: "sharedDriver",
  calendar: "sharedCalendar",
  notification: "sharedNotification",
  command: "commandTitle",
  maintenance: "sharedMaintenance",
  permission: "sharedPermissions",
  server: "settingsServer",
  position: "positionTitle",
  attribute: "sharedAttribute",
}

// Helper para obter label traduzido do objectType
const getObjectTypeLabel = (objectType: string, t: (key: string) => string): string => {
  const key = OBJECT_TYPE_TRANSLATION_KEYS[objectType.toLowerCase()]
  return key ? t(key) : objectType
}

// Estilos para highlight de diff
const DIFF_STYLES = {
  added: {
    backgroundColor: "rgba(46, 160, 67, 0.2)",
    borderLeft: "3px solid #2ea043",
    padding: "2px 6px",
    margin: "1px 0",
    borderRadius: "2px",
  },
  removed: {
    backgroundColor: "rgba(248, 81, 73, 0.2)",
    borderLeft: "3px solid #f85149",
    padding: "2px 6px",
    margin: "1px 0",
    borderRadius: "2px",
    textDecoration: "line-through",
    opacity: 0.8,
  },
  changed: {
    backgroundColor: "rgba(255, 213, 0, 0.25)",
    borderLeft: "3px solid #d4a72c",
    padding: "2px 6px",
    margin: "1px 0",
    borderRadius: "2px",
  },
  unchanged: {
    padding: "2px 6px",
    margin: "1px 0",
    opacity: 0.6,
  },
}

// Componente para renderizar diff com highlight
const DiffHighlighter = ({
  current,
  previous,
  t
}: {
  current: Record<string, any>
  previous: Record<string, any>
  t: (key: string) => string
}) => {
  const allKeys = new Set([...Object.keys(current), ...Object.keys(previous)])

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return "null"
    if (typeof value === "object") return JSON.stringify(value)
    return String(value)
  }

  const changes: { key: string; type: "added" | "removed" | "changed" | "unchanged"; oldVal?: string; newVal?: string }[] = []

  allKeys.forEach(key => {
    const hasOld = key in previous
    const hasNew = key in current
    const oldVal = formatValue(previous[key])
    const newVal = formatValue(current[key])

    if (!hasOld && hasNew) {
      changes.push({ key, type: "added", newVal })
    } else if (hasOld && !hasNew) {
      changes.push({ key, type: "removed", oldVal })
    } else if (oldVal !== newVal) {
      changes.push({ key, type: "changed", oldVal, newVal })
    } else {
      changes.push({ key, type: "unchanged", newVal })
    }
  })

  // Ordenar: alterados primeiro, depois adicionados, removidos, e inalterados por último
  const order = { changed: 0, added: 1, removed: 2, unchanged: 3 }
  changes.sort((a, b) => order[a.type] - order[b.type])

  const changedCount = changes.filter(c => c.type !== "unchanged").length

  return (
    <Box sx={{
      fontFamily: "monospace",
      fontSize: "0.75rem",
      backgroundColor: "rgba(0, 0, 0, 0.02)",
      borderRadius: "4px",
      padding: "8px",
      maxHeight: "280px",
      overflow: "auto",
    }}>
      {/* Legenda */}
      <Box sx={{ display: "flex", gap: 2, mb: 1.5, flexWrap: "wrap", borderBottom: "1px solid rgba(0,0,0,0.1)", pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, backgroundColor: "rgba(255, 213, 0, 0.5)", borderRadius: "2px" }} />
          <Typography variant="caption">{t("sharedChanged") || "Alterado"}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, backgroundColor: "rgba(46, 160, 67, 0.4)", borderRadius: "2px" }} />
          <Typography variant="caption">{t("sharedAdded") || "Adicionado"}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, backgroundColor: "rgba(248, 81, 73, 0.4)", borderRadius: "2px" }} />
          <Typography variant="caption">{t("sharedRemoved") || "Removido"}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
          {changedCount} {changedCount === 1 ? t("sharedChange") || "alteração" : t("sharedChanges") || "alterações"}
        </Typography>
      </Box>

      {/* Lista de campos */}
      {changes.map(({ key, type, oldVal, newVal }) => (
        <Box key={key} sx={{ ...DIFF_STYLES[type], display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Typography
              component="span"
              sx={{
                fontWeight: 600,
                minWidth: "120px",
                color: type === "removed" ? "error.main" : type === "added" ? "success.main" : type === "changed" ? "warning.dark" : "text.secondary"
              }}
            >
              {key}:
            </Typography>
            {type === "changed" ? (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography
                    component="span"
                    sx={{
                      backgroundColor: "rgba(248, 81, 73, 0.15)",
                      padding: "1px 4px",
                      borderRadius: "2px",
                      textDecoration: "line-through",
                      wordBreak: "break-word",
                    }}
                  >
                    {oldVal}
                  </Typography>
                  <Typography component="span" sx={{ color: "text.secondary" }}>→</Typography>
                  <Typography
                    component="span"
                    sx={{
                      backgroundColor: "rgba(46, 160, 67, 0.2)",
                      padding: "1px 4px",
                      borderRadius: "2px",
                      fontWeight: 500,
                      wordBreak: "break-word",
                    }}
                  >
                    {newVal}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography component="span" sx={{ wordBreak: "break-word", flex: 1 }}>
                {type === "removed" ? oldVal : newVal}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

const AttributesRenderer = ({ attributes }: { attributes: AuditLogAttributes }) => {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const t = useTranslation()

  const diff = attributes?.diff as AuditLogDiff | undefined

  const baseAttributes: Record<string, any> = { ...attributes }
  delete baseAttributes.diff

  const hasDiff =
    diff !== undefined &&
    ((diff.attributes && Object.keys(diff.attributes).length > 0) ||
      (diff.old && Object.keys(diff.old).length > 0) ||
      (diff.permissions && Object.keys(diff.permissions).length > 0))

  if (!hasDiff && Object.keys(baseAttributes).length === 0) {
    return <span>-</span>
  }

  const normalizedAttributes: Record<string, any> = {}
  Object.entries(baseAttributes).forEach(([key, value]) => {
    if (typeof value === "string" && (value.startsWith("[") || value.startsWith("{"))) {
      try {
        normalizedAttributes[key] = JSON.parse(value)
      } catch (error) {
        normalizedAttributes[key] = value
      }
    } else {
      normalizedAttributes[key] = value
    }
  })

  const preStyle = {
    margin: 0,
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
    fontSize: "0.75rem",
    fontFamily: "monospace",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    padding: "8px",
    borderRadius: "4px",
    maxHeight: "220px",
    overflow: "auto" as const,
  }

  const payloadForCopy = hasDiff ? diff : normalizedAttributes

  const handleCopy = async () => {
    if (!navigator.clipboard) {
      return
    }
    try {
      await navigator.clipboard.writeText(JSON.stringify(payloadForCopy, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      setCopied(false)
    }
  }

  const summaryLabel = hasDiff
    ? t('viewDiff')
    : `${t('viewDetails')} (${Object.keys(normalizedAttributes).length})`

  // Contagem de alterações para badge
  const changesCount = hasDiff
    ? new Set([...Object.keys(diff?.attributes || {}), ...Object.keys(diff?.old || {})]).size
    : 0

  return (
    <div>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton
          aria-label={open ? t('sharedHide') : t('viewDetails')}
          size="small"
          onClick={() => setOpen(!open)}
        >
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
        <Typography variant="body2">{open ? t('sharedHide') : summaryLabel}</Typography>
        {hasDiff && !open && changesCount > 0 && (
          <Chip
            label={changesCount}
            size="small"
            color="warning"
            sx={{ height: 20, fontSize: "0.7rem" }}
          />
        )}
        {copied && <Chip label={t('sharedCopied')} size="small" color="success" />}
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        {hasDiff ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Diff com highlight */}
            <DiffHighlighter
              current={diff?.attributes ?? {}}
              previous={diff?.old ?? {}}
              t={t}
            />

            {/* Permissões (se houver) */}
            {diff?.permissions && Object.keys(diff.permissions).length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{t('sharedPermissions')}</Typography>
                <pre style={preStyle}>{JSON.stringify(diff.permissions, null, 2)}</pre>
              </Box>
            )}

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={handleCopy}
              >
                {copied ? t('sharedCopied') : t('sharedCopy')}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <pre style={preStyle}>{JSON.stringify(normalizedAttributes, null, 2)}</pre>
        )}
      </Collapse>
    </div>
  )
}

const ActionsReportPage = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const classes = useReportStyles()
  const t = useTranslation()
  const tokens = usePremiumTokens()
  const [logs, setLogs] = useState<AuditLogItem[]>([])
  const [todosLogs, setTodosLogs] = useState<AuditLogItem[]>([])
  const [loading, setLoading] = useState(false)
  const [usersLoading, setUsersLoading] = useState(false)
  const [hasResults, setHasResults] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [userIdFilter, setUserIdFilter] = useState("")
  const [deviceIdFilter, setDeviceIdFilter] = useState("")
  const [periodoFilter, setPeriodoFilter] = useState<{ from: string; to: string }>({ from: "", to: "" })
  const [usuarios, setUsuarios] = useState<User[]>([])
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  const printing = useSelector((state) => state.session.printing)
  const usersMap = useSelector((state) => state.usersMap ?? EMPTY_USERS_MAP, shallowEqual)
  const devices = useSelector((state) => state.devices?.items ?? EMPTY_DEVICE_MAP, shallowEqual)

  const [available, setAvailable] = useState([])
  const [columns, setColumns] = useState([
    "id",
    "userId",
    "actionType",
    "objectType",
    "address",
    "actionTime",
    "attributes",
    "actions",
  ])
  const [showRemovalsOnly, setShowRemovalsOnly] = useState(false)
  const [objectTypeFilter, setObjectTypeFilter] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedAction, setSelectedAction] = useState<AuditLogItem | null>(null)
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false)
  const [feedback, setFeedback] =
    useState<{ open: boolean; message: string; severity: "success" | "error" }>({
      open: false,
      message: "",
      severity: "success",
    })

  const selectedUserName = userIdFilter ? usersMap[userIdFilter] ?? userIdFilter : undefined
  const selectedDeviceName = deviceIdFilter
    ? devices[Number(deviceIdFilter)]?.name || devices[Number(deviceIdFilter)]?.uniqueId || deviceIdFilter
    : undefined

  // Extrair objectTypes únicos dos dados carregados
  const availableObjectTypes = useMemo(() => {
    const types = new Set<string>()
    todosLogs.forEach((log) => {
      if (log.objectType) {
        types.add(log.objectType)
      }
    })
    return Array.from(types).sort()
  }, [todosLogs])

  // Detectar padrões suspeitos de segurança
  const securityAlerts = useMemo(() => {
    if (logs.length === 0) return []

    const alerts: { type: string; message: string; severity: "warning" | "error" }[] = []

    // Agrupar por usuário e ordenar por tempo
    const byUser: Record<number, AuditLogItem[]> = {}
    logs.forEach((log) => {
      if (!byUser[log.userId]) byUser[log.userId] = []
      byUser[log.userId].push(log)
    })

    Object.entries(byUser).forEach(([userId, userLogs]) => {
      const userName = usersMap[Number(userId)] || `User ${userId}`

      // Ordenar por tempo
      const sorted = [...userLogs].sort(
        (a, b) => new Date(a.actionTime).getTime() - new Date(b.actionTime).getTime()
      )

      // 1. Múltiplas exclusões em sequência (>5 em 1 minuto)
      const removes = sorted.filter((l) => l.actionType === "remove")
      for (let i = 0; i < removes.length - 4; i++) {
        const first = new Date(removes[i].actionTime).getTime()
        const fifth = new Date(removes[i + 4].actionTime).getTime()
        if (fifth - first <= 60000) {
          // 1 minuto
          alerts.push({
            type: "mass_delete",
            message: t("securityAlertMassDelete").replace("{user}", userName).replace("{count}", "5+"),
            severity: "error",
          })
          break
        }
      }

      // 2. Alterações em massa (>20 ações em 5 min)
      for (let i = 0; i < sorted.length - 19; i++) {
        const first = new Date(sorted[i].actionTime).getTime()
        const twentieth = new Date(sorted[i + 19].actionTime).getTime()
        if (twentieth - first <= 300000) {
          // 5 minutos
          alerts.push({
            type: "mass_actions",
            message: t("securityAlertMassActions").replace("{user}", userName).replace("{count}", "20+"),
            severity: "warning",
          })
          break
        }
      }
    })

    // 3. Ações fora do horário comercial (22h-6h)
    const afterHoursActions = logs.filter((log) => {
      const hour = new Date(log.actionTime).getHours()
      return hour >= 22 || hour < 6
    })
    if (afterHoursActions.length > 0) {
      alerts.push({
        type: "after_hours",
        message: t("securityAlertAfterHours").replace("{count}", afterHoursActions.length.toString()),
        severity: "warning",
      })
    }

    return alerts
  }, [logs, usersMap, t])

  // Stats por tipo de ação e objectType
  const stats = useMemo(() => {
    if (logs.length === 0) return null

    const creates = logs.filter((l) => l.actionType === "create").length
    const updates = logs.filter((l) => l.actionType === "update").length
    const removes = logs.filter((l) => l.actionType === "remove").length
    const uniqueUserIds = new Set(logs.map((l) => l.userId)).size

    // Stats por objectType
    const byObjectType: Record<string, number> = {}
    logs.forEach((log) => {
      const type = log.objectType || "unknown"
      byObjectType[type] = (byObjectType[type] || 0) + 1
    })

    return {
      total: logs.length,
      creates,
      updates,
      removes,
      uniqueUsers: uniqueUserIds,
      byObjectType,
    }
  }, [logs])

  useEffect(() => {
    if (printing) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => nativePostMessage(`print|${t("systemLogReport")}.pdf`)),
      )
    }
  }, [printing, t])

  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false))
    }
    window.addEventListener("afterprint", handleAfterPrint)
    return () => {
      window.removeEventListener("afterprint", handleAfterPrint)
    }
  }, [])

  useEffect(() => {
    const fetchUsers = async () => {
      const limit = 500
      try {
        setUsersLoading(true)
        // standardized filter section: carrega usuários acessíveis para dropdown otimizado.
        const response = await fetchOrThrow(`/api/users?all=false&limit=${limit}`) // switched to fetchOrThrow
        const data: User[] = await response.json()

        const userNamesMap = {}
        const sanitizedUsers: User[] = []
        data.forEach((user) => {
          if (typeof user.id !== "number") {
            return
          }
          const displayName = user.name || user.email || `${user.id}`
          userNamesMap[user.id] = displayName
          sanitizedUsers.push({ id: user.id, name: displayName, email: user.email })
        })

        dispatch({
          type: "usersMap/update",
          payload: userNamesMap,
        })

        setUsuarios(sanitizedUsers)

        if (sanitizedUsers.length >= limit) {
          console.warn(
            `Limite de ${limit} usuários atingido ao carregar opções de filtro. Considere refinar os critérios.`,
          )
        }
      } catch (error) {
        console.error("Erro ao buscar usuários:", error)
      } finally {
        setUsersLoading(false)
      }
    }

    fetchUsers()
  }, [dispatch])

  // preserved custom logic: mantém agregações locais e exportações específicas enquanto padroniza filtros.
  const buscarLogs = async (filtros: Record<string, any> = {}) => {
    setHasResults(false)
    setLoading(true)
    try {
      const filtrosComUsuario = { ...filtros }
      if (!filtrosComUsuario.userId && userIdFilter) {
        filtrosComUsuario.userId = userIdFilter
      }
      if (!filtrosComUsuario.deviceId && deviceIdFilter) {
        filtrosComUsuario.deviceId = deviceIdFilter
      }

      const query = new URLSearchParams()
      Object.entries(filtrosComUsuario).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "" || value === false) {
          return
        }

        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null && item !== "") {
              query.append(key, item.toString())
            }
          })
        } else {
          query.append(key, value.toString())
        }
      })

      const periodoAplicado = {
        from: filtrosComUsuario.from ?? periodoFilter.from,
        to: filtrosComUsuario.to ?? periodoFilter.to,
      }

      const endpoint = query.toString() ? `/api/actions?${query.toString()}` : "/api/actions"
      const resposta = await fetchOrThrow(endpoint) // switched to fetchOrThrow
      const dados = await resposta.json()
      setTodosLogs(dados)

      const usuarioAplicado = Array.isArray(filtrosComUsuario.userId)
        ? filtrosComUsuario.userId[0]?.toString() ?? ""
        : filtrosComUsuario.userId?.toString() ?? userIdFilter
      const dispositivoAplicado = Array.isArray(filtrosComUsuario.deviceId)
        ? filtrosComUsuario.deviceId[0]?.toString() ?? ""
        : filtrosComUsuario.deviceId?.toString() ?? deviceIdFilter

      const dadosFiltrados = aplicarFiltrosLocais(
        dados,
        usuarioAplicado,
        periodoAplicado,
        showRemovalsOnly,
        dispositivoAplicado,
      )
      setLogs(dadosFiltrados)
      setHasResults(dadosFiltrados.length > 0)

      setPage(0)

      if (dados.length > 0) {
        const keySet = new Set()
        dados.forEach((log: AuditLogItem) => {
          Object.keys(log).forEach((key) => keySet.add(key))
        })
        keySet.add("actions")

        setAvailable(
          [...keySet]
            .map((key) => [key, getColumnDisplayName(key as string)])
            .sort((a, b) => a[1].localeCompare(b[1])),
        )
      }
    } catch (erro) {
      if (erro instanceof Error) {
        dispatch(errorsActions.push(erro.message))
      }
      setHasResults(false)
    } finally {
      setLoading(false)
    }
  }

  const aplicarFiltrosLocais = (
    logs: AuditLogItem[],
    userId: string,
    periodo: { from: string; to: string },
    removalsOnly = showRemovalsOnly,
    deviceId: string = deviceIdFilter,
    objectType: string = objectTypeFilter,
    search: string = searchTerm,
  ) => {
    // preserved custom logic: filtragem adicional em memória para recursos existentes (ex.: impressões, destaque de remoções).
    let resultado = [...logs]

    if (userId) {
      resultado = resultado.filter((log) => log.userId.toString() === userId)
    }

    if (deviceId) {
      resultado = resultado.filter(
        (log) =>
          typeof log.objectType === "string" &&
          log.objectType.toLowerCase() === "device" &&
          log.objectId?.toString() === deviceId,
      )
    }

    // Filtro por objectType
    if (objectType) {
      resultado = resultado.filter(
        (log) => log.objectType?.toLowerCase() === objectType.toLowerCase()
      )
    }

    // Busca textual
    if (search && search.trim()) {
      const term = search.toLowerCase().trim()
      resultado = resultado.filter((log) => {
        // Buscar no objectType
        if (log.objectType?.toLowerCase().includes(term)) return true
        // Buscar no nome do usuário
        const userName = usersMap[log.userId]
        if (userName && userName.toLowerCase().includes(term)) return true
        // Buscar nos atributos (JSON)
        if (log.attributes) {
          const attrStr = JSON.stringify(log.attributes).toLowerCase()
          if (attrStr.includes(term)) return true
        }
        // Buscar no endereço
        if (log.address?.toLowerCase().includes(term)) return true
        return false
      })
    }

    if (periodo.from || periodo.to) {
      resultado = resultado.filter((log) => {
        const dataLog = new Date(log.actionTime)

        if (periodo.from && periodo.to) {
          const dataInicio = new Date(periodo.from)
          const dataFim = new Date(periodo.to)
          dataFim.setHours(23, 59, 59, 999)

          return dataLog >= dataInicio && dataLog <= dataFim
        } else if (periodo.from) {
          const dataInicio = new Date(periodo.from)
          return dataLog >= dataInicio
        } else if (periodo.to) {
          const dataFim = new Date(periodo.to)
          dataFim.setHours(23, 59, 59, 999)
          return dataLog <= dataFim
        }

        return true
      })
    }

    if (removalsOnly) {
      resultado = resultado.filter((log) => log.actionType === "remove")
    }

    return resultado
  }

  const filtrarPorUserId = (userId) => {
    setUserIdFilter(userId)
    const dadosFiltrados = aplicarFiltrosLocais(todosLogs, userId, periodoFilter, showRemovalsOnly, deviceIdFilter, objectTypeFilter, searchTerm)
    setLogs(dadosFiltrados)
    setHasResults(dadosFiltrados.length > 0)
    setPage(0)
  }

  const handleToggleRemovals = (checked: boolean) => {
    setShowRemovalsOnly(checked)
    const dadosFiltrados = aplicarFiltrosLocais(todosLogs, userIdFilter, periodoFilter, checked, deviceIdFilter, objectTypeFilter, searchTerm)
    setLogs(dadosFiltrados)
    setHasResults(dadosFiltrados.length > 0)
    setPage(0)
  }

  const handleDeviceFilterChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value ?? ""
    setDeviceIdFilter(value)
    const dadosFiltrados = aplicarFiltrosLocais(todosLogs, userIdFilter, periodoFilter, showRemovalsOnly, value, objectTypeFilter, searchTerm)
    setLogs(dadosFiltrados)
    setHasResults(dadosFiltrados.length > 0)
    setPage(0)
  }

  const handleObjectTypeFilterChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value ?? ""
    setObjectTypeFilter(value)
    const dadosFiltrados = aplicarFiltrosLocais(todosLogs, userIdFilter, periodoFilter, showRemovalsOnly, deviceIdFilter, value, searchTerm)
    setLogs(dadosFiltrados)
    setHasResults(dadosFiltrados.length > 0)
    setPage(0)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchTerm(value)
    const dadosFiltrados = aplicarFiltrosLocais(todosLogs, userIdFilter, periodoFilter, showRemovalsOnly, deviceIdFilter, objectTypeFilter, value)
    setLogs(dadosFiltrados)
    setHasResults(dadosFiltrados.length > 0)
    setPage(0)
  }

  const openRestoreDialog = (log: AuditLogItem) => {
    setSelectedAction(log)
    setRestoreDialogOpen(true)
  }

  const handleDialogClose = () => {
    setRestoreDialogOpen(false)
    setSelectedAction(null)
  }

  const handleRestored = (message?: string) => {
    setFeedback({ open: true, message: message || t("recoverySuccess"), severity: "success" })
    buscarLogs()
    handleDialogClose()
  }

  const handleRestoreError = (message: string) => {
    setFeedback({ open: true, message, severity: "error" })
  }

  const handleFeedbackClose = () => {
    setFeedback((prev) => ({ ...prev, open: false }))
  }

  const exportRemovals = () => {
    const removals = logs.filter((log) => log.actionType === "remove")
    if (!removals.length) {
      setFeedback({ open: true, message: t("noRecordsExport"), severity: "error" })
      return
    }

    const dataset = removals.map((log) => ({
      ID: log.id,
      [t("settingsUser")]: usersMap[log.userId] || log.userId,
      [t("sharedType")]: `${log.objectType ? getObjectTypeLabel(log.objectType, t) : "-"}#${log.objectId ?? "-"}`,
      [t("positionFixTime")]: formatarDataHora(log.actionTime),
      [t("sharedAttributes")]: formatAttributesForPrinting(log.attributes),
    }))

    const worksheet = utils.json_to_sheet(dataset)
    const workbook = utils.book_new()
    utils.book_append_sheet(workbook, worksheet, "Exclusoes")
    writeFileXLSX(workbook, "exclusoes_auditoria.xlsx")
  }

  const handleExportReport = (_params: { from?: string; to?: string } = {}) => {
    // Export avançado com múltiplas abas
    if (!logs.length) {
      setFeedback({ open: true, message: t("noRecordsExport"), severity: "error" })
      return
    }

    const workbook = utils.book_new()

    // Função helper para formatar logs para Excel
    const formatLogForExcel = (log: AuditLogItem) => ({
      ID: log.id,
      [t("settingsUser")]: usersMap[log.userId] || log.userId,
      [t("sharedActionType")]: log.actionType,
      [t("sharedType")]: log.objectType ? getObjectTypeLabel(log.objectType, t) : "-",
      [t("deviceIdentifier")]: log.objectId || "-",
      [t("positionAddress")]: log.address || "-",
      [t("positionFixTime")]: formatarDataHora(log.actionTime),
      [t("sharedAttributes")]: formatAttributesForPrinting(log.attributes),
    })

    // Aba 1: Resumo
    const summaryData = [
      { [t("sharedTotal")]: logs.length },
      { [t("actionCreate")]: logs.filter(l => l.actionType === "create").length },
      { [t("actionUpdate")]: logs.filter(l => l.actionType === "update").length },
      { [t("actionRemove")]: logs.filter(l => l.actionType === "remove").length },
      { [t("uniqueUsers")]: new Set(logs.map(l => l.userId)).size },
    ]
    // Adicionar contagem por objectType
    const objectTypeCounts: Record<string, number> = {}
    logs.forEach(log => {
      const type = log.objectType || "unknown"
      objectTypeCounts[type] = (objectTypeCounts[type] || 0) + 1
    })
    Object.entries(objectTypeCounts).forEach(([type, count]) => {
      summaryData.push({ [`${t("sharedType")}: ${getObjectTypeLabel(type, t)}`]: count })
    })
    const summarySheet = utils.json_to_sheet(summaryData)
    utils.book_append_sheet(workbook, summarySheet, t("sharedTotal").substring(0, 31))

    // Aba 2: Todos os Logs
    const allLogsSheet = utils.json_to_sheet(logs.map(formatLogForExcel))
    utils.book_append_sheet(workbook, allLogsSheet, t("reportActions").substring(0, 31))

    // Aba 3: Criações
    const creates = logs.filter(l => l.actionType === "create")
    if (creates.length > 0) {
      const createsSheet = utils.json_to_sheet(creates.map(formatLogForExcel))
      utils.book_append_sheet(workbook, createsSheet, t("actionCreate").substring(0, 31))
    }

    // Aba 4: Atualizações
    const updates = logs.filter(l => l.actionType === "update")
    if (updates.length > 0) {
      const updatesSheet = utils.json_to_sheet(updates.map(formatLogForExcel))
      utils.book_append_sheet(workbook, updatesSheet, t("actionUpdate").substring(0, 31))
    }

    // Aba 5: Exclusões (com dados originais)
    const removes = logs.filter(l => l.actionType === "remove")
    if (removes.length > 0) {
      const removesData = removes.map(log => ({
        ...formatLogForExcel(log),
        [t("recoveryOriginalData")]: log.attributes?.diff?.old ? JSON.stringify(log.attributes.diff.old, null, 2) : "-",
      }))
      const removesSheet = utils.json_to_sheet(removesData)
      utils.book_append_sheet(workbook, removesSheet, t("actionRemove").substring(0, 31))
    }

    // Aba 6: Por Usuário
    const userGroups: Record<string, AuditLogItem[]> = {}
    logs.forEach(log => {
      const userName = usersMap[log.userId] || `User ${log.userId}`
      if (!userGroups[userName]) userGroups[userName] = []
      userGroups[userName].push(log)
    })
    const userSummary = Object.entries(userGroups).map(([user, userLogs]) => ({
      [t("settingsUser")]: user,
      [t("sharedTotal")]: userLogs.length,
      [t("actionCreate")]: userLogs.filter(l => l.actionType === "create").length,
      [t("actionUpdate")]: userLogs.filter(l => l.actionType === "update").length,
      [t("actionRemove")]: userLogs.filter(l => l.actionType === "remove").length,
    }))
    const userSheet = utils.json_to_sheet(userSummary)
    utils.book_append_sheet(workbook, userSheet, t("settingsUser").substring(0, 31))

    writeFileXLSX(workbook, `${t("reportActions")}_${new Date().toISOString().split('T')[0]}.xlsx`)
    setFeedback({ open: true, message: t("sharedSaved"), severity: "success" })
  }

  const handlePrintReport = (params: { from?: string; to?: string; [key: string]: unknown } = {}) => {
    // standardized export/print/agenda menu – integra fluxo de impressão compartilhado.
    const { from: fromParam, to: toParam } = params
    if (!logs.length) {
      setFeedback({ open: true, message: t("noRecordsPrint"), severity: "error" })
      return
    }
    const periodoInicial = fromParam || periodoFilter.from || logs[0]?.actionTime || ""
    const periodoFinal = toParam || periodoFilter.to || logs[logs.length - 1]?.actionTime || ""
    if (periodoInicial) {
      setFrom(periodoInicial)
    }
    if (periodoFinal) {
      setTo(periodoFinal)
    }
    dispatch(sessionActions.setPrinting(true))
  }

  const getColumnDisplayName = (column: string): string => {
    switch (column) {
      case "id":
        return "ID"
      case "userId":
        return t("settingsUser")
      case "actionType":
        return t("sharedActionType")
      case "objectType":
        return t("sharedQbjectType")
      case "objectId":
        return t("deviceIdentifier")
      case "address":
        return t("positionAddress")
      case "actionTime":
        return t("positionFixTime")
      case "attributes":
        return t("sharedAttributes")
      case "actions":
        return t("sharedActions") || "Actions"
      default:
        return column
    }
  }

  useEffect(() => {
    buscarLogs()
  }, [])

  const handleChangePage = (event: unknown, novaPagina: number) => {
    setPage(novaPagina)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const formatarDataHora = (stringDataHora: string): string => {
    // Convert to Brasilia time then format via centralized formatDate
    const dateInBrasilia = new Date(new Date(stringDataHora).toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }))
    return formatDate(dateInBrasilia.toISOString())
  }

  const formatAttributesForPrinting = (attributes: AuditLogAttributes) => {
    if (!attributes) {
      return "-"
    }
    const diff = attributes.diff
    if (diff) {
      const sections: string[] = []
      if (diff.old && Object.keys(diff.old).length > 0) {
        sections.push(`Antes:\n${JSON.stringify(diff.old, null, 2)}`)
      }
      if (diff.attributes && Object.keys(diff.attributes).length > 0) {
        sections.push(`Depois:\n${JSON.stringify(diff.attributes, null, 2)}`)
      }
      if (diff.permissions && Object.keys(diff.permissions).length > 0) {
        sections.push(`Permissões:\n${JSON.stringify(diff.permissions, null, 2)}`)
      }
      return sections.length ? sections.join("\n\n") : "-"
    }
    const baseAttributes = { ...attributes }
    delete baseAttributes.diff
    return Object.keys(baseAttributes).length ? JSON.stringify(baseAttributes, null, 2) : "-"
  }

  const renderCellValue = (log: AuditLogItem, column: string) => {
    switch (column) {
      case "id":
        return log.id
      case "userId":
        return usersMap[log.userId] || `${log.userId}`
      case "actionType":
        const actionColors: Record<string, "success" | "info" | "error" | "warning" | "primary" | "default"> = {
          create: "success",
          update: "info",
          remove: "error",
          login: "primary",
          logout: "warning",
        }
        const actionLabels: Record<string, string> = {
          create: t('actionCreate'),
          update: t('actionUpdate'),
          remove: t('actionRemove'),
          login: t('actionLogin'),
          logout: t('actionLogout'),
        }
        return (
          <Chip
            label={actionLabels[log.actionType] || log.actionType}
            size="small"
            color={actionColors[log.actionType] || "default"}
            variant="filled"
          />
        )
      case "objectType":
        if (!log.objectType) return "-"
        const objectTypeColors: Record<string, "primary" | "secondary" | "success" | "info" | "warning" | "error" | "default"> = {
          device: "primary",
          user: "secondary",
          geofence: "success",
          group: "info",
          driver: "warning",
          notification: "error",
        }
        return (
          <Chip
            label={getObjectTypeLabel(log.objectType, t)}
            size="small"
            color={objectTypeColors[log.objectType.toLowerCase()] || "default"}
            variant="outlined"
            onClick={() => {
              setObjectTypeFilter(log.objectType || "")
              const dadosFiltrados = aplicarFiltrosLocais(todosLogs, userIdFilter, periodoFilter, showRemovalsOnly, deviceIdFilter, log.objectType || "", searchTerm)
              setLogs(dadosFiltrados)
              setHasResults(dadosFiltrados.length > 0)
              setPage(0)
            }}
            sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
          />
        )
      case "objectId":
        return log.objectId || "-"
      case "address":
        return log.address
      case "actionTime":
        return formatarDataHora(log.actionTime)
      case "attributes":
        if (printing) {
          return formatAttributesForPrinting(log.attributes)
        }

        return <AttributesRenderer attributes={log.attributes} />
      case "actions":
        if (log.actionType === "remove" && log.attributes?.diff) {
          return (
            <Tooltip title={t("recoveryRestore")}>
              <span>
                <IconButton size="small" color="primary" onClick={() => openRestoreDialog(log)}>
                  <RestoreIcon fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
          )
        }
        return "-"
      default:
        return log[column] !== undefined ? log[column] : "-"
    }
  }

  const handleSubmit = useCatch(async ({ from, to }) => {
    const usuarioSelecionado = userIdFilter
    const dispositivoSelecionado = deviceIdFilter

    if (!usuarioSelecionado && !dispositivoSelecionado) {
      setFeedback({
        open: true,
        message: t("selectFilterRequired"),
        severity: "error",
      })
      setLogs([])
      setTodosLogs([])
      setAvailable([])
      setHasResults(false)
      setPage(0)
      return
    }

    const periodoAtualizado = { from: from ?? "", to: to ?? "" }
    setPeriodoFilter(periodoAtualizado)
    setLogs([])
  setTodosLogs([])
  setAvailable([])
  setHasResults(false)
  setPage(0)

  const filtros: Record<string, any> = {}
  if (periodoAtualizado.from) filtros.from = periodoAtualizado.from
  if (periodoAtualizado.to) filtros.to = periodoAtualizado.to
  if (usuarioSelecionado) filtros.userId = usuarioSelecionado
  if (dispositivoSelecionado) filtros.deviceId = dispositivoSelecionado

  await buscarLogs(filtros)
})

  const handleSchedule = useCatch(async (userIds, groupIds, report) => {
    report.type = "logs"
    navigate("/reports/scheduled")
  })


  if (printing) {
    return (
      <PrintLayout
        title={t("systemLogReport")}
        from={from}
        to={to}
        deviceName={selectedDeviceName}
        onReady={() => window.print()}
      >
        <Table className="print-table">
          <TableHead>
            <TableRow>
              {columns.map((key) => (
                <TableCell key={key}>{getColumnDisplayName(key)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id}>
                {columns.map((column) => (
                  <TableCell key={`${log.id}-${column}`}>{renderCellValue(log, column)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PrintLayout>
    )
  }

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={["reportTitle", "reportActions"]}>
      <div className={classes.container}>
        <div className={classes.containerMain}>
          <div className={classes.header}>
            {/* standardized export/print/agenda menu */}
            <ReportFilter
              onShow={handleSubmit}
              onExport={handleExportReport}
              onPrint={handlePrintReport}
              onSchedule={handleSchedule}
              loading={loading}
              canRun={Boolean(userIdFilter || deviceIdFilter)}
              hasResults={hasResults}
              ignoreDevice
              showOnly={null}
              includeExportOptions
            >
              <>
                <Box className={classes.filterItem} sx={{ order: -2, minWidth: 200 }}>
                  <FormControl fullWidth size="small" variant="outlined" sx={FILTER_FORM_CONTROL_SX}>
                    <InputLabel id="device-select-label">{t("sharedDevice")}</InputLabel>
                    <Select
                      labelId="device-select-label"
                      value={deviceIdFilter}
                      label={t("sharedDevice")}
                      onChange={handleDeviceFilterChange}
                      displayEmpty
                      size="small"
                      sx={FILTER_SELECT_SX}
                    >
                      <MenuItem value="">
                        <em>{t("sharedAllDevices") || "Todos os dispositivos"}</em>
                      </MenuItem>
                      {Object.values(devices).map((device: any) => (
                        <MenuItem key={device.id} value={device.id?.toString() ?? ""}>
                          {device.name || device.uniqueId || device.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box className={classes.filterItem} sx={{ order: -1, minWidth: 200 }}>
                  {usersLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <UserNameFilter onChange={filtrarPorUserId} users={usuarios} value={userIdFilter} />
                  )}
                </Box>

                {/* Filtro por Tipo de Objeto */}
                <Box className={classes.filterItem} sx={{ order: 0, minWidth: 180 }}>
                  <FormControl fullWidth size="small" variant="outlined" sx={FILTER_FORM_CONTROL_SX}>
                    <InputLabel id="objecttype-select-label">{t("sharedType")}</InputLabel>
                    <Select
                      labelId="objecttype-select-label"
                      value={objectTypeFilter}
                      label={t("sharedType")}
                      onChange={handleObjectTypeFilterChange}
                      displayEmpty
                      size="small"
                      sx={FILTER_SELECT_SX}
                      startAdornment={objectTypeFilter ? <CategoryIcon sx={{ mr: 0.5, color: 'action.active' }} fontSize="small" /> : null}
                    >
                      <MenuItem value="">
                        <em>{t("sharedAll")}</em>
                      </MenuItem>
                      {availableObjectTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {getObjectTypeLabel(type, t)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Campo de Busca */}
                <Box className={classes.filterItem} sx={{ order: 0.5, minWidth: 200 }}>
                  <TextField
                    size="small"
                    placeholder={t("sharedSearch")}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      ...FILTER_FORM_CONTROL_SX,
                      width: '100%',
                    }}
                  />
                </Box>

                <Box className={classes.filterItem} sx={{ order: 1, minWidth: 200 }}>
                  <ColumnSelect
                    columns={columns}
                    setColumns={setColumns}
                    columnsArray={available}
                    rawValues
                    disabled={!logs.length}
                  />
                </Box>

                <Box className={classes.filterItem} sx={{ order: 2, minWidth: 200 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showRemovalsOnly}
                        onChange={(event) => handleToggleRemovals(event.target.checked)}
                      />
                    }
                    label={t('reportShowRemovalsOnly')}
                    sx={{ whiteSpace: "nowrap" }}
                  />
                </Box>

                <Box className={classes.filterItem} sx={{ order: 3, display: "flex", justifyContent: "flex-end", minWidth: 200 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<DownloadIcon />}
                    onClick={exportRemovals}
                    disabled={!logs.some((log) => log.actionType === "remove")}
                    sx={{ minWidth: 160 }}
                  >
                    {t('reportExportRemovals')}
                  </Button>
                </Box>
              </>
            </ReportFilter>
          </div>

          {/* Stats Cards - Ações */}
          {stats && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', px: 2, pb: 0.5, pt: 1, backgroundColor: tokens.surface.elevated, borderTop: `1px solid ${tokens.border.subtle}` }}>
              <Chip
                icon={<AssessmentIcon sx={{ color: `${tokens.accent.primary} !important` }} />}
                label={`${stats.total} ${t('sharedTotal')}`}
                sx={{ backgroundColor: tokens.accent.subtle, color: tokens.accent.primary, borderColor: tokens.accent.primary, fontWeight: 500 }}
                variant="outlined"
              />
              <Chip
                icon={<AddCircleIcon sx={{ color: `${tokens.semantic.success} !important` }} />}
                label={`${stats.creates} ${t('actionCreate')}`}
                sx={{ borderColor: tokens.semantic.success, color: tokens.semantic.success }}
                variant="outlined"
              />
              <Chip
                icon={<EditIcon sx={{ color: `${tokens.semantic.info} !important` }} />}
                label={`${stats.updates} ${t('actionUpdate')}`}
                sx={{ borderColor: tokens.semantic.info, color: tokens.semantic.info }}
                variant="outlined"
              />
              <Chip
                icon={<DeleteIcon sx={{ color: `${tokens.semantic.error} !important` }} />}
                label={`${stats.removes} ${t('actionRemove')}`}
                sx={{ borderColor: tokens.semantic.error, color: tokens.semantic.error }}
                variant="outlined"
              />
              <Chip
                icon={<PersonIcon sx={{ color: `${tokens.text.secondary} !important` }} />}
                label={`${stats.uniqueUsers} ${t('uniqueUsers')}`}
                sx={{ borderColor: tokens.border.default, color: tokens.text.primary }}
                variant="outlined"
              />
            </Box>
          )}

          {/* Stats Cards - Por Tipo de Objeto */}
          {stats && stats.byObjectType && Object.keys(stats.byObjectType).length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', px: 2, pb: 1, pt: 0.5, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                {t('sharedType')}:
              </Typography>
              {Object.entries(stats.byObjectType)
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .slice(0, 8)
                .map(([type, count]) => {
                  const objectTypeColors: Record<string, "primary" | "secondary" | "success" | "info" | "warning" | "error" | "default"> = {
                    device: "primary",
                    user: "secondary",
                    geofence: "success",
                    group: "info",
                    driver: "warning",
                    notification: "error",
                  }
                  return (
                    <Chip
                      key={type}
                      label={`${getObjectTypeLabel(type, t)}: ${count}`}
                      size="small"
                      color={objectTypeColors[type.toLowerCase()] || "default"}
                      variant="outlined"
                      onClick={() => {
                        setObjectTypeFilter(type)
                        const dadosFiltrados = aplicarFiltrosLocais(todosLogs, userIdFilter, periodoFilter, showRemovalsOnly, deviceIdFilter, type, searchTerm)
                        setLogs(dadosFiltrados)
                        setHasResults(dadosFiltrados.length > 0)
                        setPage(0)
                      }}
                      sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                    />
                  )
                })}
            </Box>
          )}

          {/* Alertas de Segurança */}
          {securityAlerts.length > 0 && (
            <Box sx={{ px: 2, pb: 1 }}>
              {securityAlerts.map((alert, index) => (
                <Alert
                  key={`${alert.type}-${index}`}
                  severity={alert.severity}
                  icon={alert.severity === "error" ? <SecurityIcon /> : <WarningAmberIcon />}
                  sx={{ mb: 0.5 }}
                >
                  {alert.message}
                </Alert>
              ))}
            </Box>
          )}

          <Table>
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell key={key}>{getColumnDisplayName(key)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading ? (
                logs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((log) => (
                  <TableRow
                    key={log.id}
                    hover
                    sx={log.actionType === "remove" ? { backgroundColor: "rgba(244, 67, 54, 0.08)" } : undefined}
                  >
                    {columns.map((column) => (
                      <TableCell key={`${log.id}-${column}`}>{renderCellValue(log, column)}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableShimmer columns={columns.length} />
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={logs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t("sharedRowsPerPage")}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("sharedOf")} ${count}`}
          />
          <RecoveryDialog
            open={restoreDialogOpen}
            action={selectedAction}
            onClose={handleDialogClose}
            onRestored={handleRestored}
            onError={handleRestoreError}
          />
          <Snackbar
            open={feedback.open}
            autoHideDuration={4000}
            onClose={handleFeedbackClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert onClose={handleFeedbackClose} severity={feedback.severity} sx={{ width: "100%" }}>
              {feedback.message}
            </Alert>
          </Snackbar>
        </div>
      </div>
    </PageLayout>
  )
}

export default ActionsReportPage
